import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Loader2, Landmark, Banknote, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';
import { EventPayments } from './EventPayments';
import { PromoDrops } from './PromoDrops';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).replace(',', '') : '';

export function EventFinancials({ eventId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/events/${eventId}/financials`, auth())).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load financials'); }
  }, [eventId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="py-16 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>;

  const connectStripe = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/organizations/me/wallet/stripe/onboard`, {}, auth());
      window.open(r.data.onboarding_url, '_blank');
      toast.success('Stripe onboarding opened in a new tab — come back when done');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start Stripe onboarding'); }
    finally { setBusy(false); }
  };

  const withdraw = async () => {
    const bal = data.wallet?.balance_cents || 0;
    if (bal < 100) return toast.error('Nothing to withdraw yet');
    if (!window.confirm(`Withdraw ${usd(bal)} to your bank? Standard payouts are free and arrive in 1-2 business days.`)) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/organizations/me/wallet/withdraw`, { amount_cents: bal }, auth());
      toast.success(`${usd(r.data.amount_cents)} on its way to your bank`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Withdrawal failed'); }
    finally { setBusy(false); }
  };

  const t = data.totals;
  const cards = [
    { label: 'Your net', value: usd(t.organizer_net_cents), hot: true },
    { label: 'Gross sales', value: usd(t.gross_cents) },
    { label: 'Eventism fees', value: usd(t.eventism_fees_cents) },
    { label: 'Refunds & Kick Backs', value: usd(t.refunds_cents) },
    { label: 'Released to wallet', value: usd(data.released_cents) },
    { label: 'Pending release', value: usd(data.pending_cents) },
    ...(data.held_pending_cents > 0 ? [{ label: 'On hold until after event', value: usd(data.held_pending_cents), hold: true }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="event-financials">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {cards.map(c => (
          <Card key={c.label} className={c.hot ? 'border-emerald-500/50' : c.hold ? 'border-amber-500/50' : 'border-border/60'}>
            <CardContent className="px-3 py-2.5">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</p>
              <p className={`text-base font-bold ${c.hot ? 'text-emerald-400' : c.hold ? 'text-amber-400' : ''}`}>{c.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-indigo-500/30"><CardContent className="py-3 flex flex-wrap items-center gap-3">
        <Clock className="h-4 w-4 text-indigo-400 shrink-0" />
        <div className="text-xs">
          <p className="font-semibold">Payout plan: {data.schedule.rolling_pct}% of every sale releases ~{data.schedule.clearance_hours}h after the card clears; the held {100 - data.schedule.rolling_pct}% releases {data.schedule.held_release}.</p>
          <p className="text-muted-foreground" data-testid="fin-next-release">
            {data.next_release_at ? `Next release: ${String(data.next_release_at).slice(0, 16).replace('T', ' ')} UTC (${usd(data.pending_cents - data.held_pending_cents)} rolling + ${usd(data.held_pending_cents)} held)` : 'Nothing pending — all earned funds are released.'}
          </p>
        </div>
      </CardContent></Card>

      <div className="grid sm:grid-cols-2 gap-3">
        <Card><CardContent className="py-4 space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Banknote className="h-4 w-4 text-emerald-400" /> Eventism wallet</p>
          <p className="text-2xl font-extrabold" data-testid="fin-wallet-balance">{usd(data.wallet?.balance_cents)}</p>
          <p className="text-[11px] text-muted-foreground">Lifetime earned: {usd(data.wallet?.lifetime_earned_cents)}</p>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy || !data.stripe.payouts_enabled || (data.wallet?.balance_cents || 0) < 100}
            onClick={withdraw} data-testid="fin-withdraw-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Withdraw to bank'}
          </Button>
          {!data.stripe.payouts_enabled && <p className="text-[10px] text-amber-400">Connect your bank below to enable withdrawals.</p>}
        </CardContent></Card>

        <Card><CardContent className="py-4 space-y-2">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Landmark className="h-4 w-4 text-indigo-400" /> Bank connection (Stripe)</p>
          {data.stripe.payouts_enabled ? (
            <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30" data-testid="fin-stripe-status"><CheckCircle2 className="h-3 w-3 mr-1" /> Live — payouts enabled</Badge>
          ) : data.stripe.connected ? (
            <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30" data-testid="fin-stripe-status"><AlertTriangle className="h-3 w-3 mr-1" /> Started — finish onboarding</Badge>
          ) : (
            <Badge variant="outline" data-testid="fin-stripe-status">Not connected</Badge>
          )}
          <p className="text-[11px] text-muted-foreground">Standard payouts (free) land in 1–2 business days.</p>
          <Button size="sm" variant="outline" disabled={busy} onClick={connectStripe} data-testid="fin-stripe-connect">
            {data.stripe.connected ? 'Manage / finish onboarding' : 'Connect bank account'}
          </Button>
        </CardContent></Card>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">Payout history <span className="text-xs font-normal text-muted-foreground">— this event</span></p>
          <a href="/payout-history" className="text-xs text-indigo-400 hover:underline" data-testid="fin-full-history-link">View full payout history →</a>
        </div>
        <div className="rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="fin-history-table">
            <thead><tr className="bg-muted/40">
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Note</th><th className="text-right px-3 py-2">Amount</th>
            </tr></thead>
            <tbody>
              {data.history.length === 0 ? (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground text-xs">No releases for this event yet — money appears here ~48h after each sale clears.</td></tr>
              ) : data.history.map(h => (
                <tr key={h.txn_id} className="border-t border-border/60">
                  <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(h.created_at)}</td>
                  <td className="px-3 py-1.5">{{ withdrawal: 'Withdrawal', payment: 'Payment sent', transfer: 'Credit sent' }[h.kind] || 'Ticket earnings'}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{h.note}</td>
                  <td className={`px-3 py-1.5 text-right font-semibold ${h.amount_cents < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{usd(Math.abs(h.amount_cents))}{h.amount_cents < 0 ? ' out' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="border-t border-border/60 pt-4">
        <EventPayments eventId={eventId} walletBalanceCents={data.wallet?.balance_cents || 0} onPaid={load} />
      </div>

      <div className="border-t border-border/60 pt-4">
        <p className="text-sm font-semibold mb-2">Re-engage your crowd</p>
        <PromoDrops eventId={eventId} walletBalanceCents={data.wallet?.balance_cents || 0} onSent={load} />
      </div>
    </div>
  );
}
